import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import { useScrollRoot } from '@/design';

/**
 * A long page's table of contents, kept in view while the page scrolls under it — the Team Portal
 * study's sticky section nav, made a component for the person hub and then for a response's
 * answers. The study itself was deleted when the Team Portal superseded it (the two-portals
 * design, §5).
 *
 * One scrolling page with this over or beside it, rather than tabs: a reviewer comparing a number
 * with the registry's answer and then with a survey moves down one page and never loses where
 * they were, and every section stays findable by a search of the page.
 *
 * Two shapes. **`bar`**, the hub's since the two portals: a strip across the top of the record,
 * sticky under the Team Portal's header on a desktop, scrolling sideways where the entries do not
 * fit — the record keeps its whole width, which a column beside it took. **`column`**, a response's
 * answers: its sections down the left, which is how a questionnaire reads.
 *
 * **Each entry is a link to its section's anchor, and a press scrolls rather than navigates.**
 * A fragment navigation is a new history entry with no state, and a record's way back reads the
 * search it came from out of that state — so following the browser's default would take the
 * "Back to the search" link away on the first press. The `href` stays, so an entry can still be
 * copied or opened in a tab.
 *
 * **The entry marked is the section nearest the top**, by an `IntersectionObserver` over the
 * anchors — `aria-current="location"`, which is what a screen reader announces for "you are
 * here". A section is judged by a band just under whatever is sticky above it, so a long one keeps
 * its mark while it fills the screen and hands it on as the next one reaches the top. Two
 * exceptions, both for a page's last sections, which can be too short ever to reach that band:
 * **a press keeps its mark** until the page scrolls on from where the press left it — the scroll
 * it makes moves the section above into the band, and the observer's answer for it, a frame
 * later, used to take the mark from the section just pressed (the hub's Notes, every time on a
 * phone) — and **at the end of a page that has been scrolled the last section in view is
 * marked**, where the band would name one above it. (Not at load: a page no taller than the screen is at its end there, and its
 * first section is the one being read.)
 *
 * **A press opens a folded section before scrolling to it** — the target when it is a closed
 * `<details>` (a `RecordSection` puts its anchor on its own), and every closed one around it — or
 * the press would scroll to a summary and leave what it named shut. Opened this way, a section is
 * open for the visit only: `RecordSection` remembers the viewer's own press, never the nav's.
 * Every hub section is such a fold since the advisor record page's F11 (Q2 ruled (a)).
 *
 * **An entry may carry a count** — Salesforce's link bar's "Surveys (3)" — drawn inside the link
 * and named with it ("Surveys, 3", F11's R10), so the entry is still one link that scrolls. The
 * caller says which entries count: only where the number is survey-api's or the page holds the
 * list whole (F11's Q3), never a sum here.
 */

export interface ISectionNavEntry {
  /** The section's `id` — `Section`'s `anchor`. */
  anchor: string;
  label: string;
  /** Absent: the entry draws no count — not zero. */
  count?: number;
}

export function SectionNav({
  entries,
  variant = 'bar',
  label = 'On this page'
}: {
  entries: ISectionNavEntry[];
  variant?: 'bar' | 'column';
  label?: string;
}) {
  const [current, setCurrent] = React.useState(entries[0]?.anchor ?? '');
  // Where the record scrolls: the window under the app bar, or a sheet's own body (SR-530's record
  // in a sheet), whose top nothing is stuck over.
  const scroll = useScrollRoot();
  const scroller = scroll.element;
  const key = entries.map((entry) => entry.anchor).join(' ');
  /** Where the last press left the page, holding its mark until the page scrolls from there — by
   *  wheel, scrollbar, keys, find-in-page or another link's `scrollIntoView` alike. */
  const pressedAt = React.useRef<number | null>(null);
  // The bar's own height: one line, or two once its entries wrap on a wide screen (SR-406). What
  // is sticky above a section is the header and this, so the band a section is judged in, and the
  // `scroll-margin` a press lands it at (`--section-bar-h`, read by `Section`), follow it.
  const bar = React.useRef<HTMLElement>(null);
  const [barHeight, setBarHeight] = React.useState(BAR_PX);
  // No bar is drawn for one entry (below); measured again when one comes or goes.
  const hidden = entries.length < 2;
  React.useEffect(() => {
    const element = bar.current;
    if (!element) return;
    const root = document.documentElement;
    const measure = () => {
      const height = Math.round(element.getBoundingClientRect().height);
      setBarHeight(height);
      root.style.setProperty('--section-bar-h', `${height}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--section-bar-h');
    };
  }, [hidden]);

  React.useEffect(() => {
    const visible = new Set<string>();
    const order = key.split(' ');
    const scrolledY = () => (scroller ? scroller.scrollTop : window.scrollY);
    const viewHeight = () => (scroller ? scroller.clientHeight : window.innerHeight);
    const mark = () => {
      if (pressedAt.current !== null) {
        if (Math.abs(scrolledY() - pressedAt.current) <= 1) return;
        pressedAt.current = null;
      }
      const root = scroller ?? document.documentElement;
      const atEnd = scrolledY() > 0 && scrolledY() + viewHeight() >= root.scrollHeight - 1;
      const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
      const last = atEnd
        ? [...order].reverse().find((anchor) => {
            const top = document.getElementById(anchor)?.getBoundingClientRect().top;
            return top !== undefined && top < bottom;
          })
        : undefined;
      const marked = last ?? order.find((anchor) => visible.has(anchor));
      if (marked) setCurrent(marked);
    };
    const observer = new IntersectionObserver(
      (changes) => {
        for (const change of changes) {
          if (change.isIntersecting) visible.add(change.target.id);
          else visible.delete(change.target.id);
        }
        mark();
      },
      // The top fifth of the viewport, under the header and the bar: the section a reader is
      // reading, not one peeking in, nor one scrolled up behind what is sticky.
      { root: scroller, rootMargin: `-${scroll.top + barHeight + 8}px 0px -80% 0px` }
    );
    for (const anchor of order) {
      const target = document.getElementById(anchor);
      if (target) observer.observe(target);
    }
    // The page's end is reached without any section crossing the band, and a press is let go by
    // the first scroll away from where it left the page.
    const target: Window | HTMLElement = scroller ?? window;
    target.addEventListener('scroll', mark, { passive: true });
    return () => {
      observer.disconnect();
      target.removeEventListener('scroll', mark);
    };
  }, [key, barHeight, scroller, scroll.top]);

  const go = (event: React.MouseEvent, anchor: string) => {
    event.preventDefault();
    const target = document.getElementById(anchor);
    if (target) {
      openFolds(target);
      // Instant, not smooth: `scrollY` is final here, which is what the press is held at.
      target.scrollIntoView({ block: 'start' });
    }
    pressedAt.current = scroller ? scroller.scrollTop : window.scrollY;
    setCurrent(anchor);
  };

  if (variant === 'column') {
    return (
      <nav aria-label={label} className="hidden lg:block">
        {/* Under the hub's bar where a record page draws one (`--section-bar-h`, SR-431's
            answers on a survey tab); under the header alone on a response. */}
        <ul className="sticky top-[calc(var(--sticky-top,3rem)+1rem+var(--section-bar-h,0px))] grid gap-0.5">
          {entries.map(({ anchor, label: entry, count }) => (
            <li key={anchor}>
              <a
                href={`#${anchor}`}
                aria-label={countedName(entry, count)}
                aria-current={current === anchor ? 'location' : undefined}
                onClick={(event) => go(event, anchor)}
                className={cn(
                  'block rounded-md px-2 py-1.5 text-[0.8125rem] text-muted-foreground hover:bg-[var(--accent)]',
                  'hover:text-foreground',
                  current === anchor &&
                    'bg-[var(--accent)] font-medium text-[var(--color-royal-blue)] shadow-[inset_2px_0_0_var(--color-teal)]'
                )}
              >
                {entry}
                <Count count={count} />
              </a>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  // One section has nowhere to jump to — a survey tab holding Veritant research alone (SR-407).
  if (hidden) return null;

  return (
    <nav
      ref={bar}
      aria-label={label}
      className="z-10 -mx-6 border-b border-border bg-white/95 px-6 backdrop-blur [contain:inline-size] lg:sticky lg:top-[var(--sticky-top,3rem)]"
    >
      {/* Sideways scrolling on a narrow screen, its bar hidden and no vertical one (the links' 1 px
          underline overlap made one); on a wide screen the entries wrap onto a second line rather
          than hide past the edge — Lightning's related-list links wrap the same way (SR-406).
          Drawn as small pills after "Jump to", not underlined tabs: under the record's own tabs a
          second row of tabs read as a second level of them, though this one only scrolls
          (SR-407). */}
      <ul className="flex [scrollbar-width:none] items-center gap-1.5 overflow-x-auto overflow-y-hidden py-2 lg:flex-wrap lg:overflow-visible">
        <li aria-hidden className="shrink-0 pr-1 text-xs text-muted-foreground">
          Jump to
        </li>
        {entries.map(({ anchor, label: entry, count }) => (
          <li key={anchor} className="shrink-0">
            <a
              href={`#${anchor}`}
              aria-label={countedName(entry, count)}
              aria-current={current === anchor ? 'location' : undefined}
              onClick={(event) => go(event, anchor)}
              className={cn(
                'block rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap transition-colors',
                current === anchor
                  ? 'border-[var(--color-teal)] bg-[var(--accent)] font-medium text-[var(--color-royal-blue)]'
                  : 'border-border text-muted-foreground hover:text-[var(--color-royal-blue)]'
              )}
            >
              {entry}
              <Count count={count} />
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** An entry's name with its count — "Surveys, 3" — or none of its own where it counts nothing. */
function countedName(label: string, count: number | undefined): string | undefined {
  return count === undefined ? undefined : `${label}, ${count}`;
}

/** The count beside an entry's words; the link's own name says it, so it is drawn only. */
function Count({ count }: { count: number | undefined }) {
  if (count === undefined) return null;
  return (
    <span
      data-count
      className="ml-1.5 rounded-full bg-[var(--accent)] px-1.5 py-px text-[0.6875rem] font-normal text-muted-foreground tabular-nums"
    >
      {count}
    </span>
  );
}

/** Opens `target` if it is a closed `<details>`, and every closed one it sits inside. Each open
 *  fires the element's own `toggle`, which is how a `RecordSection` learns of it — so a fold
 *  opened this way is open for the visit and not remembered. Exported for the hub's other ways to
 *  a place inside a section ("Write a note", a disclosure revealed), which must open a folded
 *  section on the way as the nav does. */
export function openFolds(target: HTMLElement) {
  let fold = target.closest('details');
  while (fold) {
    fold.open = true;
    fold = fold.parentElement?.closest('details') ?? null;
  }
}

/** The bar's height on one line (its measured height once drawn), which with what is stuck over the
 *  scroller — the Team Portal's 48 px header on a page (`useScrollRoot`), nothing in a sheet — a
 *  section's `scroll-margin` clears too (`Section`, through `--section-bar-h` and `--sticky-top`),
 *  with 8 to spare. */
const BAR_PX = 40;
